import { Card, Empty, Icon, Space, Text, type IconName } from "@app/ui";
import { useT, useFormatDateTime, type MessageKey } from "../i18n.js";
import type { SelfActivityEvent } from "../types.js";

/**
 * The account's own slice of the audit trail. Same events security teams read in
 * /audit, but self-scoped and permission-free: a user should be able to see that
 * someone tried their password at 03:00 without being an auditor.
 */

/**
 * Presentation for the actions a user can cause on their own account. The audit
 * vocabulary grows faster than this table does, so an unmapped action falls back
 * to its raw key with a neutral glyph rather than disappearing from the feed.
 */
const EVENTS: Record<string, { icon: IconName; label: MessageKey }> = {
  "auth.login.success": { icon: "log-in", label: "activityAction.auth.login.success" },
  "auth.login.failure": { icon: "log-in", label: "activityAction.auth.login.failure" },
  "auth.logout": { icon: "log-out", label: "activityAction.auth.logout" },
  "auth.account.locked": { icon: "lock", label: "activityAction.auth.account.locked" },
  "auth.token.reuse_detected": { icon: "warning", label: "activityAction.auth.token.reuse_detected" },
  "auth.password.changed": { icon: "key", label: "activityAction.auth.password.changed" },
  "auth.password.reset": { icon: "key", label: "activityAction.auth.password.reset" },
  "auth.password.reset_requested": { icon: "key", label: "activityAction.auth.password.reset_requested" },
  "mfa.enrolled": { icon: "shield", label: "activityAction.mfa.enrolled" },
  "mfa.disabled": { icon: "shield", label: "activityAction.mfa.disabled" },
  "mfa.challenge.success": { icon: "shield", label: "activityAction.mfa.challenge.success" },
  "mfa.challenge.failure": { icon: "shield", label: "activityAction.mfa.challenge.failure" },
  "session.revoked": { icon: "monitor", label: "activityAction.session.revoked" },
  "session.revoked_all": { icon: "monitor", label: "activityAction.session.revoked_all" },
  "user.updated": { icon: "user", label: "activityAction.user.updated" },
  "user.email_verified": { icon: "mail", label: "activityAction.user.email_verified" },
  "user.verification_requested": { icon: "mail", label: "activityAction.user.verification_requested" },
  "org.invite_accepted": { icon: "building", label: "activityAction.org.invite_accepted" },
};

export function ActivityTab({ events }: { events: SelfActivityEvent[] }) {
  const t = useT();
  const fmtDate = useFormatDateTime();

  return (
    <Card title={t("profile.activity")}>
      <Space direction="vertical" size="md" className="w-full">
        <Text small secondary>
          {t("profile.activityHint")}
        </Text>

        {events.length === 0 ? (
          <Empty description={t("profile.activityEmpty")} />
        ) : (
          <div className="pf-feed">
            {events.map((e) => {
              const known = EVENTS[e.action];
              const failed = e.outcome !== "success";
              return (
                <div key={e.seq} className="pf-feed__item">
                  <span className={`pf-feed__mark${failed ? " is-failure" : ""}`}>
                    <Icon name={known?.icon ?? "info"} size={13} />
                  </span>
                  <div className="pf-feed__body">
                    <span className="pf-feed__title">{known ? t(known.label) : e.action}</span>
                    <div className="pf-feed__meta">
                      <span>{fmtDate(e.createdAt)}</span>
                      {e.ip && <span>{`${t("common.ip")}: ${e.ip}`}</span>}
                      {failed && <span>{t("audit.failure")}</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Space>
    </Card>
  );
}
